import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

//Components
import { Modal, ModalBody, ModalFooter, Button, Table, Tabs, TabContainer, Tooltip } from "@gpstrackit/react-controls";
import { Grid, Chip, Icon, IconButton } from "@mui/material";
import { NotificationManager } from 'Components/Notifications';

//Actions
import {
    vidfleetInformationSettingsRequest,
    vidfleetInformationCloseModal,
    createClipRequestOpenModal,
    vidfleetInformationRequest,
    vidfleetGetClipTimeRequest,
} from "Redux/actions";

import { useIntl } from "react-intl";

//Helpers
import { getDateFormatted } from 'Core/data/Helpers';
import { HasPermission } from 'Core/security';

//Views
import DetailView from "Components/DetailView";
import CreateClipRequest from 'Modules/devices/.components/createClipRequest';

//Styles
import "./index.scss";

const VidfleetInformation = (props) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();

    //Local States
    const [preloaded, setPreloaded] = useState(true);
    const [data, setData] = useState([])
    const [updatedOn, setUpdatedOn] = useState(null);
    const [tab, setTab] = useState(0);


    //Redux States
    const { vidFleetDeviceInfoResult, loadingGetVidFleetInfo, resultSettigs, openModal, esn, deviceId, resultClipTime, timezone, clips } = useSelector(state => state.deviceInfoRedux);
    const createClipRequestOpenModalV = useSelector(state => state.createClipRequestRedux.openModal);
    const result = useSelector(state => state.createClipRequestRedux.result);
    const { user } = useSelector(state => state.securityRedux);

    const closeModal = () => {
        dispatch(vidfleetInformationCloseModal());
        setData([])
        setTab(0);
        setUpdatedOn(null);
    }

    //functions
    const getErrorMessage = (code) => {
        let codes = {
            23: messages["vidfleet_complete_error_23"],
            24: messages["vidfleet_complete_error_24"],
            200: messages["vidfleet_complete_error_200"],
            201: messages["vidfleet_complete_error_201"],
            202: messages["vidfleet_complete_error_202"],
            204: messages["vidfleet_complete_error_204"],
            209: messages["vidfleet_complete_error_202"],
            "default": messages["vidfleet_device_info_error_default"],
        }
        return codes[code] || codes["default"];
    }

    const handleTabs = (newTab) => {
        setTab(newTab)
        if (newTab === 1) {
            dispatch(vidfleetInformationSettingsRequest(esn))
        }
        if (newTab === 2) {
            dispatch(vidfleetGetClipTimeRequest(esn, deviceId, preloaded))
        }
    }

    const calculateTimeElapsed = (initial, final) => {
        // get total seconds between the times
        let delta = Math.abs(new Date(final) - new Date(initial)) / 1000;
        // calculate (and subtract) whole days
        let days = Math.floor(delta / 86400);
        delta -= days * 86400;
        // calculate (and subtract) whole hours
        let hours = Math.floor(delta / 3600) % 24;
        delta -= hours * 3600;
        // calculate (and subtract) whole minutes
        let minutes = Math.floor(delta / 60) % 60;
        delta -= minutes * 60;
        // what's left is seconds
        var seconds = delta % 60;
        return `${days ? `${days} days, ` : ''}${hours ? `${hours} hrs ` : ''}${minutes ? `${minutes} mins` : ''}${seconds ? ` ${Math.floor(seconds)} sec` : ''}`
    }

    const handlerPreloaded = (value) => {
        setPreloaded(value)
        dispatch(vidfleetGetClipTimeRequest(esn, deviceId, value))
    }

    useEffect(() => {
        if (openModal && esn) {
            dispatch(vidfleetInformationRequest(esn))
        }
    }, [openModal, esn]);

    useEffect(() => {
        if (vidFleetDeviceInfoResult?.error) {
            closeModal();
            let currentMessage = getErrorMessage(vidFleetDeviceInfoResult?.error?.code);
            NotificationManager.error(
                currentMessage,
                messages['vidfleet_information'],
                3000,
                null,
                null,
                'error'
            );
        }
    }, [vidFleetDeviceInfoResult]);

    useEffect(() => {
        if (resultClipTime?.validTimeRangeSeq?.length) {
            let data = resultClipTime?.validTimeRangeSeq?.reverse()
            setData(data);
        }
        if (resultClipTime?.updatedOn) {
            setUpdatedOn(getDateFormatted(resultClipTime?.updatedOn, "date", "datetime", null, timezone))
        }
    }, [resultClipTime])

    useEffect(() => {
        if (openModal && clips) {
            handleTabs(2)
        }
    }, [clips])

    useEffect(() => {
        if (result && clips) {
            closeModal()
        }
    }, [result])

    let innerHeight = window.innerHeight;

    return (
        <>
            <Modal
                open={openModal}
                title={messages['vidfleet_information']}
                handleClose={() => closeModal()}
                position="right"
                loading={loadingGetVidFleetInfo}
            >
                <ModalBody className="body-device-info">
                    <Tabs tab={tab} onChange={(event) => handleTabs(event)}>
                        <TabContainer index={0} label="General">
                            <div style={{ padding: '5px 10px', marginTop: 60, marginBottom: 30 }}>
                                <DetailView data={vidFleetDeviceInfoResult} />
                            </div>
                        </TabContainer>
                        <TabContainer index={1} label={messages['vidfleet_settings']}>
                            <div style={{ padding: '5px 10px', marginTop: 50, marginBottom: 10 }}>
                                {
                                    resultSettigs?.eventThreshold &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['event_threshold']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.eventThreshold} />
                                        </Grid>
                                        {resultSettigs?.eventThreshold?.steps &&
                                            <Grid item xs={12}>
                                                <DetailView data={{ steps: JSON.stringify(resultSettigs?.eventThreshold?.steps) }} />
                                            </Grid>
                                        }
                                    </Grid>
                                }
                                {
                                    resultSettigs?.eventLevel &&
                                    <>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_dms']}</p>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <DetailView data={resultSettigs?.eventLevel?.DMS} />
                                            </Grid>
                                        </Grid>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_adas']}</p>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <DetailView data={resultSettigs?.eventLevel?.ADAS} />
                                            </Grid>
                                        </Grid>
                                    </>

                                }
                                {
                                    resultSettigs?.sounds &&
                                    <>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_sounds']}</p>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <DetailView data={{ ...resultSettigs?.sounds, dms: null, adas: null, }} />
                                            </Grid>
                                        </Grid>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_dms']}</p>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <DetailView data={resultSettigs?.sounds?.dms} />
                                            </Grid>
                                        </Grid>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_adas']}</p>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <DetailView data={resultSettigs?.sounds?.adas} />
                                            </Grid>
                                        </Grid>
                                    </>
                                }
                                {
                                    resultSettigs?.videoStreams &&
                                    <>
                                        <div style={{ margin: '10px 0', fontWeight: 500 }}>
                                            <p>Video Streams</p>

                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Stream 0</p>
                                            </div>
                                            <DetailView data={resultSettigs?.videoStreams[0]} />
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Stream 1</p>
                                            </div>
                                            <DetailView data={resultSettigs?.videoStreams[1]} />
                                        </div>
                                    </>
                                }
                                {
                                    resultSettigs?.delayedPowerOff &&
                                    <DetailView data={{ delayedPowerOff: resultSettigs?.delayedPowerOff }} />
                                }
                                {
                                    resultSettigs?.rotate &&
                                    <DetailView data={{ rotate: resultSettigs?.rotate }} />
                                }

                                {
                                    resultSettigs?.nightVision &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_night_vision']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.nightVision} />
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    resultSettigs?.mic &&
                                    <DetailView data={{ mic: resultSettigs?.mic }} />
                                }
                                {
                                    resultSettigs?.autoSnapshot &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_auto_snapshot']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.autoSnapshot} />
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    resultSettigs?.trustAcc &&
                                    <DetailView data={{ trustAcc: resultSettigs?.trustAcc }} />
                                }
                                {
                                    resultSettigs?.liveStreamDuration &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_live_stream_duration']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={{ liveStreamDuration: resultSettigs?.liveStreamDuration }} />
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    resultSettigs?.recordConfig &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_record_config']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.recordConfig} />
                                        </Grid>
                                    </Grid>
                                }
                                {resultSettigs?.markedClipDuration &&
                                    <>
                                        <div style={{ margin: '10px 0', fontWeight: 500 }}>
                                            <p>Marked Clip Duration</p>
                                        </div>
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>Accel</p>
                                        </div>
                                        <DetailView data={resultSettigs?.markedClipDuration?.accel} />
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>Manual</p>
                                        </div>
                                        <DetailView data={resultSettigs?.markedClipDuration?.manual} />
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>DMS</p>
                                        </div>
                                        <DetailView data={resultSettigs?.markedClipDuration?.dms} />
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>ADAS</p>
                                        </div>
                                        <DetailView data={resultSettigs?.markedClipDuration?.adas} />
                                    </>
                                }
                                {
                                    resultSettigs?.timezone &&
                                    <DetailView data={{ timezone: resultSettigs?.timezone }} />
                                }
                                {
                                    resultSettigs?.privacyMask &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_privacy_mask']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.privacyMask} />
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    resultSettigs?.obdII &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>ObdII</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={{ mode: resultSettigs?.obdII?.mode }} />
                                            <DetailView data={{ voltage: resultSettigs?.obdII?.voltage }} />
                                        </Grid>
                                    </Grid>
                                }
                                {resultSettigs?.dms &&
                                    <>
                                        <div style={{ margin: '10px 0', fontWeight: 500 }}>
                                            <p>DMS</p>
                                            <DetailView data={{ Level: resultSettigs?.dms?.level }} />
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Min Speed</p>
                                            </div>
                                            <DetailView data={resultSettigs?.dms?.minSpeed} />
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Detection Delay</p>
                                            </div>
                                            <DetailView data={resultSettigs?.dms?.detectionDelay} />
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Repeat Interval</p>
                                            </div>
                                            <DetailView data={resultSettigs?.dms?.repeatInterval} />
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Detection Reset</p>
                                            </div>
                                            <DetailView data={resultSettigs?.dms?.detectionReset} />
                                            <DetailView data={{ isLeftRudder: resultSettigs?.dms.isLeftRudder }} />
                                        </div>
                                    </>
                                }
                                {
                                    resultSettigs?.volume &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_volume']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.volume} />
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    resultSettigs?.runningInfo &&
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5 }}>{messages['vidfleet_running_info']}</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetailView data={resultSettigs?.runningInfo} />
                                        </Grid>
                                    </Grid>
                                }
                                {resultSettigs?.advanced &&
                                    <>
                                        <div style={{ margin: '10px 0', fontWeight: 500 }}>
                                            <p>Advanced</p>
                                        </div>
                                        {
                                            resultSettigs?.advanced?.aux &&
                                            <div style={{ margin: '10px 0', fontSize: 14 }}>
                                                <p>Aux</p>
                                            </div>
                                        }
                                        {
                                            resultSettigs?.advanced?.aux &&
                                            <DetailView data={resultSettigs?.advanced?.aux} />
                                        }
                                        <DetailView data={{ sdCardEventSpace: resultSettigs?.advanced?.sdCardEventSpace }} />
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>ADAS</p>
                                        </div>
                                        <DetailView data={resultSettigs?.advanced?.adas} />
                                        <div style={{ margin: '10px 0', fontSize: 14 }}>
                                            <p>Ignition</p>
                                        </div>
                                        <DetailView data={{ ignition: resultSettigs?.advanced?.ignition }} />
                                        {
                                            resultSettigs?.advanced?.lang &&
                                            <DetailView data={{ lang: resultSettigs?.advanced?.lang }} />
                                        }
                                    </>
                                }
                            </div>
                        </TabContainer>
                        <TabContainer index={2} label={messages['vidfleet_available_clips']}>

                            <div style={{ padding: '5px 10px', marginTop: 50, display: 'flex' }}>
                                <div style={{ width: '50%' }}>
                                    {
                                        resultClipTime?.cameraSN &&
                                        <DetailView data={{ ESN: resultClipTime?.cameraSN, [messages['status']]: resultClipTime?.status }} />
                                    }
                                    {
                                        resultClipTime?.esn &&
                                        <DetailView data={{ ESN: resultClipTime?.esn }} />
                                    }
                                </div>
                                <div style={{ width: '50%', display: 'flex', justifyContent: 'end' }}>
                                    <Chip
                                        style={preloaded ? {
                                            backgroundColor: '#66bb6a',
                                            color: '#ffff',
                                            marginRight: 5
                                        } : { marginRight: 5 }}
                                        className={preloaded ? "calamp-info-selected" : ""}
                                        label={messages['vidfleet_from_db']}
                                        onClick={() => {
                                            setData([])
                                            handlerPreloaded(true)
                                            setUpdatedOn(null)
                                        }
                                        }
                                    />
                                    <Chip
                                        style={!preloaded ? {
                                            backgroundColor: '#66bb6a',
                                            color: '#ffff'
                                        } : {}}
                                        className={!preloaded ? "calamp-info-selected" : ""}
                                        label={messages['vidfleet_from_device']}
                                        onClick={() => {
                                            setData([])
                                            handlerPreloaded(false)
                                            setUpdatedOn(null)
                                        }}
                                    />
                                </div>
                            </div>
                            {updatedOn &&
                                <div style={{ padding: '5px 10px', display: 'flex' }}>
                                    <p style={{ fontSize: 15, marginBottom: 5 }}>{`${messages['vidfleet_updated_on']}  `}</p>
                                    <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 5, marginLeft: 5 }}>{`${updatedOn}`}</p>
                                </div>
                            }
                            <div style={{ padding: '0 10px' }}>
                                {
                                    data && data.length > 0 ?
                                        <div className="available-clips-table">
                                            <Table
                                                columns={
                                                    [
                                                        {
                                                            key: "create-clip",
                                                            title: "",
                                                            show: true,
                                                            render: (item) => {
                                                                return (
                                                                    <Tooltip title={messages['create_clip_request_title']}>
                                                                        <IconButton
                                                                            disabled={!HasPermission(user, "clpr", "c")}
                                                                            onClick={() => dispatch(createClipRequestOpenModal(item))}
                                                                        >
                                                                            <Icon>add_circle</Icon>
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )
                                                            }
                                                        },
                                                        {
                                                            key: "start-time",
                                                            title: messages['vidfleet_start_time'],
                                                            show: true,
                                                            render: (item) => {
                                                                return (
                                                                    <div>{item[0] && getDateFormatted(item[0], "date", "datetime", null, timezone)}</div>
                                                                )
                                                            }
                                                        },
                                                        {
                                                            key: "end-time",
                                                            title: messages['vidfleet_end_time'],
                                                            show: true,
                                                            render: (item) => {
                                                                return (
                                                                    <div>{item[1] && getDateFormatted(item[1], "date", "datetime", null, timezone)}</div>
                                                                )
                                                            }
                                                        },
                                                        {
                                                            key: "duration",
                                                            title: messages['vidfleet_duration'],
                                                            show: true,
                                                            render: (item) => {
                                                                let time = item[0] && item[1] && calculateTimeElapsed(item[0], item[1])
                                                                return (
                                                                    <span>{time}</span>
                                                                )
                                                            }
                                                        }
                                                    ]
                                                }
                                                data={data}
                                                disableItemSelection
                                                hidePagination
                                                height={innerHeight - 325}
                                            />
                                        </div>
                                        : <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>{messages['vidfleet_noclipsavailable']}</div>
                                }
                            </div>
                        </TabContainer>
                    </Tabs>
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="btn-dark-blue"
                        onClick={() => closeModal()}
                    >
                        {messages['close']}
                    </Button>
                    {
                        tab === 2 &&
                        <Button
                            className="btn-cancel"
                            onClick={() => dispatch(vidfleetGetClipTimeRequest(esn, deviceId, preloaded))}
                        >
                            {messages['refresh']}
                        </Button>
                    }
                </ModalFooter>
            </Modal>
            {createClipRequestOpenModalV && <CreateClipRequest />}
        </>
    )
}
export default VidfleetInformation